import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Loader2, Search, X } from 'lucide-react';

export interface SearchableOption {
  value: string;
  label: string;
  /** Right-aligned hint, e.g. a role or a column type. */
  hint?: string;
  /** Extra text the search box also matches against. */
  keywords?: string;
}

interface Props {
  options: SearchableOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Shown on the trigger while nothing is selected. */
  emptyLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Debounce applied to the typed search term, in ms. */
  searchDebounceMs?: number;
  /** Filters the options already held in memory. */
  searchable?: boolean;
  /** Receives the debounced term so a caller can fetch remote options. */
  onSearch?: (term: string) => void;
  className?: string;
  id?: string;
}

/**
 * A dropdown with a search box on top of it.
 *
 * A native <select> cannot be filtered once its option list passes a few dozen
 * entries, which is the normal case for both the table list and the grant
 * target list, so those are picked from this instead. The search is client side
 * over whatever options the caller holds; if the caller also needs the server
 * to narrow the list it passes onSearch and replaces the options.
 */
const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  emptyLabel = 'Select',
  disabled = false,
  loading = false,
  searchDebounceMs = 250,
  searchable = true,
  onSearch,
  className = '',
  id,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // A remote list is fetched when the panel opens and again on every debounced
  // term change. Nothing is requested while the panel is shut.
  useEffect(() => {
    if (!onSearch || !open) return;
    const timer = setTimeout(() => onSearch(term.trim()), searchDebounceMs);
    return () => clearTimeout(timer);
  }, [term, onSearch, searchDebounceMs, open]);

  // A remote list has already been narrowed by the server, so re-filtering it
  // locally would hide matches on fields the caller cannot see.
  const filtered = useMemo(() => {
    if (!searchable || onSearch) return options;
    const needle = term.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) =>
      `${option.label} ${option.hint ?? ''} ${option.keywords ?? ''}`.toLowerCase().includes(needle)
    );
  }, [options, term, searchable, onSearch]);

  const selected = useMemo(() => options.find((option) => option.value === value) ?? null, [options, value]);

  useEffect(() => {
    setHighlight(0);
  }, [filtered.length, open]);

  const close = useCallback(() => {
    setOpen(false);
    setTerm('');
  }, []);

  // Clicking anywhere else dismisses the panel, so the page underneath stays usable.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, close]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.querySelector<HTMLElement>('[data-highlighted="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const choose = (option: SearchableOption) => {
    onChange(option.value);
    close();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((prev) => (filtered.length === 0 ? 0 : (prev + 1) % filtered.length));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((prev) => (filtered.length === 0 ? 0 : (prev - 1 + filtered.length) % filtered.length));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const option = filtered[highlight];
      if (option) choose(option);
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-left text-xs text-slate-200 outline-none transition focus:border-cyan-500/50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className={`truncate ${selected ? 'font-bold text-slate-100' : 'text-slate-600'}`}>
          {selected ? selected.label : emptyLabel}
        </span>
        {loading ? (
          <Loader2 size={14} className="flex-shrink-0 animate-spin text-cyan-400" />
        ) : selected ? (
          <span
            role="button"
            tabIndex={-1}
            title="Clear"
            onClick={(event) => {
              event.stopPropagation();
              onChange('');
            }}
            className="flex-shrink-0 rounded p-0.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
          >
            <X size={13} />
          </span>
        ) : (
          <ChevronDown size={14} className="flex-shrink-0 text-slate-500" />
        )}
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-40 mt-1 overflow-hidden rounded-xl border border-slate-800 bg-slate-900 shadow-2xl">
          {searchable && (
            <div className="relative border-b border-slate-800">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                ref={inputRef}
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                className="w-full bg-transparent py-2.5 pl-8 pr-3 text-xs text-slate-200 placeholder-slate-600 outline-none"
              />
            </div>
          )}

          <ul
            ref={listRef}
            role="listbox"
            className="max-h-64 overflow-y-auto py-1"
          >
            {filtered.length === 0 ? (
              <li className="px-3.5 py-4 text-center text-[11px] font-bold text-slate-500">
                {loading ? 'Searching…' : 'No match'}
              </li>
            ) : (
              filtered.map((option, index) => {
                const isSelected = option.value === value;
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-highlighted={index === highlight}
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => choose(option)}
                      className={`flex w-full items-center justify-between gap-3 px-3.5 py-2 text-left transition ${
                        index === highlight ? 'bg-slate-800/70' : ''
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {isSelected && <Check size={13} className="flex-shrink-0 text-cyan-400" />}
                        <span
                          className={`truncate text-xs ${isSelected ? 'font-black text-cyan-300' : 'font-semibold text-slate-200'}`}
                        >
                          {option.label}
                        </span>
                      </span>
                      {option.hint && (
                        <span className="flex-shrink-0 text-[10px] font-black uppercase tracking-widest text-slate-500">
                          {option.hint}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
